import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './layouts/AdminLayout';
import PortalLayout from './layouts/PortalLayout';
import PublicLayout from './layouts/PublicLayout';
import { useAuth } from './auth/AuthContext';
import { ROLES } from './auth/constants';

// Lazy load admin page components
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const CodeManagement = lazy(() => import('./pages/coding/CodeManagement'));
const CodeMapping = lazy(() => import('./pages/coding/CodeMapping'));
const BlacklistManagement = lazy(() => import('./pages/blacklist/BlacklistManagement'));
const SupplierList = lazy(() => import('./pages/supplier-archive/SupplierList'));
const SupplierArchiveDetail = lazy(() => import('./pages/supplier-archive/SupplierArchiveDetail'));
const AdmissionList = lazy(() => import('./pages/admission/AdmissionList'));
const AdmissionForm = lazy(() => import('./pages/admission/AdmissionForm'));
const SiteInspection = lazy(() => import('./pages/admission/SiteInspection'));
const SuspensionManage = lazy(() => import('./pages/supplier-archive/SuspensionManage'));
const ExecutionLedger = lazy(() => import('./pages/performance/ExecutionLedger'));
const InspectionRecord = lazy(() => import('./pages/performance/InspectionRecord'));
const IssueTracking = lazy(() => import('./pages/performance/IssueTracking'));
const RelatedPartyScreening = lazy(() => import('./pages/supervision/RelatedPartyScreening'));
const MultiDeptView = lazy(() => import('./pages/supervision/MultiDeptView'));
const EvaluationTemplate = lazy(() => import('./pages/evaluation/EvaluationTemplate'));
const DailyEvaluation = lazy(() => import('./pages/evaluation/DailyEvaluation'));
const AnnualEvaluation = lazy(() => import('./pages/evaluation/AnnualEvaluation'));
const GradingResult = lazy(() => import('./pages/evaluation/GradingResult'));
const ExitList = lazy(() => import('./pages/exit/ExitList'));
const ExitApply = lazy(() => import('./pages/exit/ExitApply'));
const RiskCenter = lazy(() => import('./pages/risk/RiskCenter'));
const ApprovalCenter = lazy(() => import('./pages/approval/ApprovalCenter'));
const AuditLog = lazy(() => import('./pages/audit/AuditLog'));
const AnnouncementManage = lazy(() => import('./pages/announcement/AnnouncementManage'));

// Lazy load portal page components
const PortalHome = lazy(() => import('./pages/portal/PortalHome'));
const SelfRegistration = lazy(() => import('./pages/portal/SelfRegistration'));
const InfoMaintenance = lazy(() => import('./pages/portal/InfoMaintenance'));
const ObjectionFeedback = lazy(() => import('./pages/portal/ObjectionFeedback'));
const EvaluationView = lazy(() => import('./pages/portal/EvaluationView'));
const AdmissionProgress = lazy(() => import('./pages/portal/AdmissionProgress'));

// Login page
const LoginPage = lazy(() => import('./pages/login/LoginPage'));

const LoadingFallback = () => (
  <div style={{ padding: 24, textAlign: 'center', color: '#999' }}>加载中...</div>
);

function RequireAuth({ children, allowedRoles }) {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to the correct home based on role
    if (user.role === ROLES.SUPPLIER) {
      return <Navigate to="/portal" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Login route */}
        <Route path="/login" element={<LoginPage />} />

        {/* Public registration route (no auth) */}
        <Route element={<PublicLayout />}>
          <Route path="/register" element={<SelfRegistration />} />
        </Route>

        {/* Admin routes - group & subsidiary */}
        <Route
          element={
            <RequireAuth allowedRoles={[ROLES.GROUP, ROLES.SUBSIDIARY]}>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/coding" element={<RequireAuth allowedRoles={[ROLES.GROUP]}><CodeManagement /></RequireAuth>} />
          <Route path="/coding/mapping" element={<RequireAuth allowedRoles={[ROLES.SUBSIDIARY]}><CodeMapping /></RequireAuth>} />
          <Route path="/blacklist" element={<BlacklistManagement />} />
          <Route path="/archive" element={<SupplierList />} />
          <Route path="/archive/detail" element={<SupplierArchiveDetail />} />
          <Route path="/admission" element={<AdmissionList />} />
          <Route path="/admission/new" element={<AdmissionForm />} />
          <Route path="/admission/inspection" element={<SiteInspection />} />
          <Route path="/supplier-suspension" element={<SuspensionManage />} />
          <Route path="/performance" element={<ExecutionLedger />} />
          <Route path="/performance/inspection" element={<InspectionRecord />} />
          <Route path="/performance/issues" element={<IssueTracking />} />
          <Route path="/supervision/related-party" element={<RelatedPartyScreening />} />
          <Route path="/supervision/multi-dept" element={<MultiDeptView />} />
          <Route path="/evaluation/template" element={<EvaluationTemplate />} />
          <Route path="/evaluation/daily" element={<DailyEvaluation />} />
          <Route path="/evaluation/annual" element={<AnnualEvaluation />} />
          <Route path="/evaluation/grading" element={<GradingResult />} />
          <Route path="/exit" element={<ExitList />} />
          <Route path="/exit/apply" element={<ExitApply />} />
          <Route path="/risk" element={<RiskCenter />} />
          <Route path="/approval" element={<ApprovalCenter />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/announcement" element={<AnnouncementManage />} />
        </Route>

        {/* Portal routes - supplier */}
        <Route
          path="/portal"
          element={
            <RequireAuth allowedRoles={[ROLES.SUPPLIER]}>
              <PortalLayout />
            </RequireAuth>
          }
        >
          <Route index element={<PortalHome />} />
          <Route path="register" element={<SelfRegistration />} />
          <Route path="admission-progress" element={<AdmissionProgress />} />
          <Route path="info" element={<InfoMaintenance />} />
          <Route path="objection" element={<ObjectionFeedback />} />
          <Route path="evaluation" element={<EvaluationView />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
